import { Site } from "@/bryntum-lib/models/Site";
import type { DomainStatus } from "@/types/siteDomain";

// --- store ---

export type SiteStoreLike = {
  query: (filter: (record: Site) => boolean, all?: boolean) => unknown;
  getById?: (id: Site["id"]) => unknown;
  add: (data: Site | Record<string, unknown>) => unknown;
};

/** Site row for the active board (one site per board). */
export function findSiteForBoard(
  store: SiteStoreLike | undefined | null,
  boardId: number,
): Site | null {
  if (!store) return null;
  const rows = store.query((record: Site) => record.board === boardId, true) as unknown as Site[];
  return rows[0] ?? null;
}

/** True when the row still needs a Bryntum sync to reach the backend. */
export function siteNeedsPersist(site: Site): boolean {
  if (site.isPhantom === true) return true;
  const id = site.id;
  if (id == null || id === ("" as unknown as number)) return true;
  if (typeof id === "string") return true;
  return false;
}

/** Phantom rows must be in the store before project.sync() can persist them. */
export function ensureSiteInStore(store: SiteStoreLike, site: Site): void {
  if (!siteNeedsPersist(site)) return;
  if (!store.getById?.(site.id)) {
    store.add(site);
  }
}

// --- url ---

export const MARKETING_PUBLIC_HOST = "swivu.site";

export function isSiteLive(site: Site | null | undefined): boolean {
  if (!site?.publishedAt) return false;
  return !Number.isNaN(new Date(site.publishedAt).getTime());
}

export function siteLiveHref(rawUrl: string): string {
  if (!rawUrl) return "";
  if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) return rawUrl;
  return `https://${rawUrl}`;
}

/** Live / custom-domain URL from the server — never constructed client-side. */
export function resolveSiteLiveUrl(site: Site | null | undefined): string {
  if (!site) return "";
  const fromApi = site.publishUrl?.trim();
  if (fromApi) return siteLiveHref(fromApi);
  return "";
}

/** Preview host URL from the server — empty until a preview has been published. */
export function resolveSitePreviewUrl(site: Site | null | undefined): string {
  if (!site) return "";
  const fromApi = site.previewUrl?.trim();
  if (fromApi) return siteLiveHref(fromApi);
  return "";
}

export {
  getSitePublishButtonState,
  getSitePublishLiveButtonState,
  mergeSitePublishFromApi,
  type SitePublishButtonState,
  type SitePublishDisabledReason,
  type SitePublishLiveButtonState,
} from "./sitePublishState";

export {
  TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG,
  isSandboxBoardRecord,
} from "./testDriveSiteRestrictions";

export function domainStatusLabel(status: DomainStatus | undefined): string {
  switch (status) {
    case "pending_verification":
      return "Pending DNS verification";
    case "provisioning":
      return "Provisioning";
    case "active":
      return "Active";
    case "failed":
      return "Setup failed";
    case "unset":
    default:
      return "Not configured";
  }
}
